import React, { useCallback, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogContent,
    FormControl,
    FormControlLabel,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import type { Colors } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { RichTextEditor } from '../../components/Common/RichTextEditor';
import { RichTextViewer } from '../../components/Common/RichTextViewer';
import {
    BRAND_LUXE_BG,
    BRAND_LUXE_PRIMARY,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_HIGH,
    BRAND_SURFACE_CONTAINER_HIGHEST,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER_LOW,
} from './brandDashboardTokens';
import { CurrencyTextInput } from '../../components/CurrencyTextInput/CurrencyTextInput';
import { DEFAULT_CURRENCY, getCurrencySymbol, supportedCurrency } from '../../constants/currency';

function formatRoleCompensationDisplay(amount: number, currencyCode: string): string {
    const code = (currencyCode || DEFAULT_CURRENCY).toUpperCase();
    const sym = getCurrencySymbol(code);
    const zeroFraction = ['JPY', 'KRW', 'VND', 'UGX', 'CLP', 'ISK'].includes(code);
    const fraction = zeroFraction ? 0 : 2;
    return `${sym}${Number(amount || 0).toLocaleString(undefined, {
        minimumFractionDigits: fraction,
        maximumFractionDigits: fraction,
    })}`;
}

const PORTFOLIO_OPTIONS_CAST = ['Headshot', 'Past work', 'Audio reel', 'Video reel', 'Full body picture'];
const PORTFOLIO_OPTIONS_CREW_TECH = ['Past work', 'Audio reel', 'Video reel'];

function portfolioOptionsForRoleGig(gig: string): string[] {
    const g = (gig || '').trim().toLowerCase();
    if (g === 'crew' || g === 'technical') return PORTFOLIO_OPTIONS_CREW_TECH;
    return PORTFOLIO_OPTIONS_CAST;
}

export interface AuditionRole {
    id: string;
    character: string;
    description: string;
    compensation: number;
    /** ISO currency code (e.g. USD) for this role's pay. */
    currency: string;
    employment_type: string;
    gig_type: string;
    compensation_type: string;
    additional_benefits: string[];
    verified_thspians_only: boolean;
    gig_expertise: string;
    portfolio_requirement: string[];
    /** Cast roles only; sent on create. Crew/technical use audition-level ages in the payload. */
    min_age: string;
    max_age: string;
}

function newAuditionRoleId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function createEmptyRole(): AuditionRole {
    return {
        id: newAuditionRoleId(),
        character: '',
        description: '',
        compensation: 0,
        currency: DEFAULT_CURRENCY,
        employment_type: '',
        gig_type: '',
        compensation_type: '',
        additional_benefits: [],
        verified_thspians_only: false,
        gig_expertise: '',
        portfolio_requirement: [],
        min_age: '',
        max_age: '',
    };
}

function getFieldSx(t: Colors) {
    return {
        '& .MuiOutlinedInput-root': {
            bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
            borderRadius: 2,
            color: t.primary_white,
            '& fieldset': { border: 'none' },
            '&:hover fieldset': { border: 'none' },
            '&.Mui-focused fieldset': { border: 'none' },
            '&.Mui-focused': { boxShadow: '0 0 0 1px rgba(236,170,0,0.35)' },
        },
        '& .MuiInputBase-input': { color: t.primary_white },
        '& .MuiInputBase-input::placeholder': { color: t.form_label, opacity: 1 },
    };
}

export interface BrandAuditionRolesStepProps {
    themeColors: Colors;
    roles: AuditionRole[];
    onRolesChange: (roles: AuditionRole[]) => void;
}

export function BrandAuditionRolesStep({ themeColors: t, roles, onRolesChange }: BrandAuditionRolesStepProps) {
    const fieldSx = getFieldSx(t);
    const labelSx = {
        fontSize: 11,
        fontFamily: FONT_ENUM.MAIN_BOLD,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.14em',
        color: 'rgba(255,255,255,0.6)',
        mb: 1,
    };

    const [editingId, setEditingId] = useState<string | null>(null);
    /** New role being composed in the modal — not appended to `roles` until Done. */
    const [draftRole, setDraftRole] = useState<AuditionRole | null>(null);
    const employmentTypes = ['Contract', 'Fulltime', 'One-off', 'Charity', 'Ongoing'];
    const gigTypes = ['Cast', 'Crew', 'Technical'];
    const compensationTypes = ['Paid', 'Unpaid', 'Tbd'];
    const gigExpertiseOptions = ['Beginner', 'Mid-level', 'Expert'];

    const isCastGig = (gig: string) => (gig || '').trim().toLowerCase() === 'cast';
    const updateRole = useCallback(
        (id: string, field: keyof AuditionRole, value: any) => {
            onRolesChange(roles.map(r => (r.id === id ? { ...r, [field]: value } : r)));
        },
        [roles, onRolesChange],
    );

    const patchModal = useCallback(
        (field: keyof AuditionRole, value: any) => {
            if (editingId) {
                updateRole(editingId, field, value);
            } else {
                setDraftRole(prev => (prev ? { ...prev, [field]: value } : null));
            }
        },
        [editingId, updateRole],
    );

    const applyGigTypeSelection = useCallback(
        (role: AuditionRole, nextGigType: string) => {
            const cast = isCastGig(nextGigType);
            const allowed = portfolioOptionsForRoleGig(nextGigType);
            const filtered = (role.portfolio_requirement || []).filter(p => allowed.includes(p));
            const next: Partial<AuditionRole> = {
                gig_type: nextGigType,
                portfolio_requirement: filtered,
                gig_expertise: cast ? '' : role.gig_expertise || 'Mid-level',
                min_age: cast ? role.min_age : '',
                max_age: cast ? role.max_age : '',
            };
            if (editingId) {
                onRolesChange(roles.map(r => (r.id === editingId ? { ...r, ...next } : r)));
            } else {
                setDraftRole(prev => (prev ? { ...prev, ...next } : null));
            }
        },
        [editingId, onRolesChange, roles],
    );

    const removeRole = useCallback(
        (id: string) => {
            onRolesChange(roles.filter(r => r.id !== id));
            if (editingId === id) setEditingId(null);
        },
        [roles, onRolesChange, editingId],
    );

    const duplicateRole = useCallback(
        (source: AuditionRole) => {
            const idx = roles.findIndex(r => r.id === source.id);
            if (idx < 0) return;
            const copy: AuditionRole = {
                ...source,
                id: newAuditionRoleId(),
                portfolio_requirement: [...(source.portfolio_requirement || [])],
                additional_benefits: [...(source.additional_benefits || [])],
            };
            const next = [...roles];
            next.splice(idx + 1, 0, copy);
            onRolesChange(next);
        },
        [roles, onRolesChange],
    );

    const addRole = useCallback(() => {
        setEditingId(null);
        setDraftRole(createEmptyRole());
    }, []);

    const closeModal = useCallback(() => {
        setDraftRole(null);
        setEditingId(null);
    }, []);

    const commitDraftAndClose = useCallback(() => {
        if (draftRole) {
            onRolesChange([...roles, draftRole]);
            setDraftRole(null);
        }
        setEditingId(null);
    }, [draftRole, roles, onRolesChange]);

    const totalRoles = roles.length;
    const roleForModal = draftRole ?? (editingId ? roles.find(r => r.id === editingId) ?? null : null);
    const roleCurrency = (role: AuditionRole) => (role.currency || DEFAULT_CURRENCY).toUpperCase();

    /** Matches Cast / Crew / Technical / Mixed chips in BrandAuditionBasicsStep (incl. MUI clickable hover). */
    const pillChipSx = (selected: boolean) => ({
        fontFamily: FONT_ENUM.MAIN_BOLD,
        fontSize: { xs: 14, sm: 15 },
        height: { xs: 42, sm: 48 },
        borderRadius: 999,
        bgcolor: selected ? BRAND_LUXE_PRIMARY : BRAND_SURFACE_CONTAINER_LOW,
        color: selected ? '#5f4200' : t.primary_white,
        justifyContent: 'center',
        px: 1,
        '& .MuiChip-label': {
            px: 1,
            width: '100%',
            textAlign: 'center',
        },
        '&:hover': { bgcolor: selected ? BRAND_LUXE_PRIMARY : '#2c2c2c' },
        '&.MuiChip-clickable:hover': { bgcolor: selected ? BRAND_LUXE_PRIMARY : '#2c2c2c' },
    });

    const renderPillGroup = (
        role: AuditionRole,
        label: string,
        options: string[],
        field: keyof AuditionRole,
        multiple = false,
    ) => (
        <Box>
            <Typography sx={labelSx}>{label}</Typography>
            <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST, p: 2, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                    {options.map(option => {
                        const selected = multiple
                            ? (role[field] as string[]).includes(option)
                            : (role[field] as string) === option;
                        return (
                            <Chip
                                key={option}
                                label={option}
                                onClick={() => {
                                    if (multiple) {
                                        const value = role[field] as string[];
                                        patchModal(
                                            field,
                                            value.includes(option)
                                                ? value.filter(v => v !== option)
                                                : [...value, option],
                                        );
                                        return;
                                    }
                                    patchModal(field, option);
                                }}
                                sx={pillChipSx(selected)}
                            />
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );

    const renderViewCard = (role: AuditionRole) => (
        <Box
            key={role.id}
            onClick={() => {
                setDraftRole(null);
                setEditingId(role.id);
            }}
            sx={{
                bgcolor: BRAND_SURFACE_CONTAINER,
                borderRadius: 2,
                p: 5,
                cursor: 'pointer',
                border: '1px solid transparent',
                transition: 'all 0.2s',
                '&:hover': { borderColor: 'rgba(255,201,101,0.1)' },
            }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1.5,
                            bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                        <PersonOutlineIcon sx={{ color: BRAND_LUXE_PRIMARY }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff', fontSize: 15 }}>
                            {role.character || 'Untitled Role'}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Per role</Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }} onClick={e => e.stopPropagation()}>
                    <IconButton
                        size="small"
                        aria-label="Duplicate role"
                        onClick={e => {
                            e.stopPropagation();
                            duplicateRole(role);
                        }}
                        sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: BRAND_LUXE_PRIMARY } }}>
                        <ContentCopyOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        aria-label="Delete role"
                        onClick={e => {
                            e.stopPropagation();
                            removeRole(role.id);
                        }}
                        sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#ff7351' } }}>
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>
            {role.description && (
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST, borderRadius: 2, p: 2, mb: 2 }}>
                    <RichTextViewer
                        content={role.description}
                        textStyle={{
                            padding: 0,
                            fontSize: 14,
                            color: 'rgba(255,255,255,0.7)',
                            lineHeight: 1.5,
                            fontStyle: 'italic',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        }}
                    />
                </Box>
            )}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    pt: 1.5,
                    borderTop: `1px solid ${BRAND_OUTLINE_VARIANT}20`,
                }}>
                <Typography
                    sx={{
                        fontSize: 12,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        color: 'rgba(255,255,255,0.4)',
                    }}>
                    Compensation
                </Typography>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 20, color: BRAND_LUXE_PRIMARY }}>
                    {formatRoleCompensationDisplay(role.compensation, roleCurrency(role))}
                </Typography>
            </Box>
            <Box sx={{ mt: 1.2, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {role.employment_type && (
                    <Chip
                        size="small"
                        label={role.employment_type}
                        sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.75)' }}
                    />
                )}
                {role.gig_type && (
                    <Chip
                        size="small"
                        label={role.gig_type}
                        sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.75)' }}
                    />
                )}
                {role.compensation_type && (
                    <Chip
                        size="small"
                        label={role.compensation_type}
                        sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.75)' }}
                    />
                )}
                {role.gig_type && isCastGig(role.gig_type) && (
                    <Chip
                        size="small"
                        label={`Ages ${role.min_age || '—'}-${role.max_age || '—'}`}
                        sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.75)' }}
                    />
                )}
            </Box>
        </Box>
    );

    const renderEditCard = (role: AuditionRole) => (
        <Box
            key={role.id}
            sx={{
                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                borderRadius: 2,
                p: 3,
                borderLeft: `4px solid ${BRAND_LUXE_PRIMARY}`,
            }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box>
                    <Typography sx={labelSx}>Role title</Typography>
                    <TextField
                        fullWidth
                        placeholder="Lead Actor, Supporting Creator..."
                        value={role.character}
                        onChange={e => patchModal('character', e.target.value)}
                        sx={{
                            ...fieldSx,
                            '& .MuiInputBase-input': {
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 15,
                                color: t.primary_white,
                            },
                        }}
                    />
                </Box>
                <Box>
                    <Typography sx={labelSx}>Role description</Typography>
                    <RichTextEditor
                        value={role.description}
                        onChange={value => patchModal('description', value)}
                        shellVariant="brandDashboard"
                    />
                </Box>
                {renderPillGroup(role, 'Select employment type', employmentTypes, 'employment_type')}
                <Box>
                    <Typography sx={labelSx}>Select gig type</Typography>
                    <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST, p: 2, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                            {gigTypes.map(option => {
                                const selected = role.gig_type === option;
                                return (
                                    <Chip
                                        key={option}
                                        label={option}
                                        onClick={() => applyGigTypeSelection(role, option)}
                                        sx={pillChipSx(selected)}
                                    />
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
                {isCastGig(role.gig_type) && (
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={labelSx}>Minimum age</Typography>
                            <TextField
                                fullWidth
                                type="number"
                                inputProps={{ min: 1, max: 120 }}
                                placeholder="e.g. 18"
                                value={role.min_age}
                                onChange={e => patchModal('min_age', e.target.value)}
                                sx={fieldSx}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={labelSx}>Maximum age</Typography>
                            <TextField
                                fullWidth
                                type="number"
                                inputProps={{ min: 1, max: 120 }}
                                placeholder="e.g. 65"
                                value={role.max_age}
                                onChange={e => patchModal('max_age', e.target.value)}
                                sx={fieldSx}
                            />
                        </Box>
                    </Box>
                )}
                {renderPillGroup(role, 'Select compensation type', compensationTypes, 'compensation_type')}
                <Box>
                    <Typography sx={labelSx}>Additional benefits</Typography>
                    <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        value={role.additional_benefits}
                        onChange={(_, values) => patchModal('additional_benefits', values)}
                        renderInput={params => (
                            <TextField {...params} placeholder="Type a benefit and press Enter" sx={fieldSx} />
                        )}
                        forcePopupIcon={false}
                        sx={{
                            '& .MuiChip-root': {
                                bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                color: t.primary_white,
                                border: `1px solid ${BRAND_OUTLINE_VARIANT}55`,
                            },
                        }}
                    />
                </Box>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={role.verified_thspians_only}
                            onChange={e => patchModal('verified_thspians_only', e.target.checked)}
                            sx={{ color: 'rgba(255,255,255,0.45)', '&.Mui-checked': { color: BRAND_LUXE_PRIMARY } }}
                        />
                    }
                    label={
                        <Typography sx={{ color: 'rgba(255,255,255,0.74)', fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                            Verified thspians only
                        </Typography>
                    }
                />
                {!isCastGig(role.gig_type) && role.gig_type && (
                    <>{renderPillGroup(role, 'Select gig expertise', gigExpertiseOptions, 'gig_expertise')}</>
                )}
                {renderPillGroup(
                    role,
                    'Portfolio requirement',
                    portfolioOptionsForRoleGig(role.gig_type),
                    'portfolio_requirement',
                    true,
                )}
                <Box>
                    <Typography sx={labelSx}>Price per role</Typography>
                    <FormControl fullWidth>
                        <CurrencyTextInput
                            key={role.id}
                            value={Number(role.compensation) || 0}
                            minAmount={0}
                            details={0}
                            supportedCurrency={supportedCurrency}
                            initialCurrency={roleCurrency(role)}
                            initialValue={Number(role.compensation) || 0}
                            brandChrome
                            onChange={({ currency, value }) => {
                                patchModal('currency', currency);
                                patchModal(
                                    'compensation',
                                    value === null || Number.isNaN(Number(value)) ? 0 : Number(value),
                                );
                            }}
                            sx={{
                                color: t.primary_white,
                                '& .MuiOutlinedInput-root': { bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST },
                                '& .MuiInputAdornment-root': {
                                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                    borderRadius: 1,
                                },
                                '& .MuiInputAdornment-root .MuiInputBase-root': { backgroundColor: 'transparent' },
                                '& .MuiInputAdornment-root .MuiSelect-select': {
                                    backgroundColor: 'transparent',
                                    color: t.primary_white,
                                },
                            }}
                        />
                    </FormControl>
                </Box>
                <Button
                    onClick={commitDraftAndClose}
                    sx={{
                        alignSelf: 'flex-end',
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        color: BRAND_LUXE_PRIMARY,
                        '&:hover': { bgcolor: 'rgba(255,201,101,0.08)' },
                    }}>
                    Done
                </Button>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    flexWrap: 'wrap',
                }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: '#fff' }}>
                    Audition Roles
                </Typography>
                {roles.length > 0 && (
                    <Typography
                        sx={{
                            fontSize: 12,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: 'rgba(255,255,255,0.6)',
                            bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                            px: 2,
                            py: 0.5,
                            borderRadius: 999,
                        }}>
                        {totalRoles} {totalRoles === 1 ? 'ROLE' : 'ROLES'} ADDED
                    </Typography>
                )}
            </Box>

            {roles.map(role => renderViewCard(role))}

            <Button
                onClick={addRole}
                sx={{
                    width: '100%',
                    py: 4,
                    borderRadius: 2,
                    border: `2px dashed ${BRAND_OUTLINE_VARIANT}50`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    textTransform: 'none',
                    color: 'rgba(255,255,255,0.5)',
                    '&:hover': { borderColor: `${BRAND_LUXE_PRIMARY}60`, color: BRAND_LUXE_PRIMARY },
                }}>
                <Box
                    sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                    <AddIcon sx={{ color: BRAND_LUXE_PRIMARY }} />
                </Box>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14 }}>Add Role</Typography>
            </Button>

            <Dialog
                open={Boolean(roleForModal)}
                onClose={closeModal}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        bgcolor: BRAND_LUXE_BG,
                        backgroundImage: 'none',
                        borderRadius: 2,
                        border: `1px solid ${BRAND_OUTLINE_VARIANT}55`,
                    },
                }}>
                <DialogContent sx={{ p: { xs: 2, sm: 3 }, position: 'relative' }}>
                    <IconButton
                        onClick={closeModal}
                        sx={{
                            position: 'absolute',
                            right: 10,
                            top: 10,
                            color: 'rgba(255,255,255,0.75)',
                            '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
                        }}>
                        <CloseIcon />
                    </IconButton>
                    {roleForModal && renderEditCard(roleForModal)}
                </DialogContent>
            </Dialog>

            {roles.length > 0 && (
                <Box
                    sx={{
                        bgcolor: 'rgba(0,253,193,0.05)',
                        border: '1px solid rgba(0,253,193,0.1)',
                        borderRadius: 3,
                        p: 3,
                        display: 'flex',
                        gap: 2,
                    }}>
                    <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 }}>
                        <strong style={{ color: '#00fdc1' }}>Pro Tip:</strong> Campaigns with clear pricing and detailed
                        role descriptions receive <strong style={{ color: '#fff' }}>45% more applicants</strong> within
                        the first 24 hours.
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
